import { type FC, useCallback, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Select, TextField, useForm } from '@travelity/form';
import { Stack } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';
import { getAssetDtoToAsset, useCreateAsset } from '@travelity/api';
import { useWatch } from 'react-hook-form';
import { useToast, useIsChanged } from '../../../hooks';
import { createAssetSchema } from './create-asset-modal.schema';
import {
    type CreateAssetModalProps,
    CreateAsset,
} from './create-asser-modal.types';
import { fuelOptions, mapVehiclesByType } from './create-asser-modal.config';
import {
    assetTypeOptions,
    vehicleGroupOptions,
} from './create-asset-modal.options';

const formId = 'create-asset-modal';

const CreateAssetModal: FC<CreateAssetModalProps> = ({
    handleCancel,
    handleConfirm,
    open,
}) => {
    const navigate = useNavigate();
    const { showError, showSuccess } = useToast();

    const { mutate: createAsset } = useCreateAsset({
        onError: () => {
            showError('CREATE_ASSET_FAILURE');
        },
    });

    const {
        Form,
        reset,
        control,
        formState: { errors },
    } = useForm<CreateAsset>({
        schema: createAssetSchema,
        isModal: true,
        mode: 'onChange',
        validateInitially: true,
        onSubmit: useCallback(
            (data: CreateAsset) => {
                createAsset(
                    {
                        vehicle_type: data.type,
                        make: data.make,
                        model: data.model,
                        year: data.year,
                        license_plate: data.licensePlate,
                        mileage: {
                            km: data.mileageStart as number,
                        },
                        max_passengers: data.maxPassengers,
                        engine_type: data.fuelType,
                    },
                    {
                        onSuccess: (asset) => {
                            handleConfirm(getAssetDtoToAsset(asset));
                            navigate('/events', { replace: true });
                            showSuccess('Vehicle Created');
                        },
                    }
                );
            },
            [navigate]
        ),
    });

    const selectedVehicleGroup = useWatch({ control, name: 'vehicleGroup' });

    const openChange = useIsChanged(open);

    useEffect(() => {
        if (openChange) reset({});
    }, [reset, openChange]);

    const availableAssetTypes = useMemo(() => {
        if (!selectedVehicleGroup) return [];

        return assetTypeOptions.filter(({ value }) =>
            mapVehiclesByType[selectedVehicleGroup].includes(value)
        );
    }, [assetTypeOptions, selectedVehicleGroup]);

    const errorFields = Object.values(errors)
        .map((error) => error?.message)
        .filter((v) => v) as string[];

    const handleDiscard = () => {
        handleCancel();
        navigate('/events', { replace: true });
    };
    return (
        <Dialog
            open={open}
            onClose={handleDiscard}
            sx={{
                '& .MuiDialog-container': {
                    justifyContent: { xs: 'center', sm: 'end' },
                },
            }}
            PaperProps={{
                sx: {
                    py: 1,
                    bgcolor: '#fff',
                    maxWidth: { xs: '100%', sm: '388px' },
                    minWidth: { xs: 'calc(100% - 32px)', sm: 'auto' },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: '#2B395B',
                }}
                id="alert-dialog-title"
            >
                Add Asset
            </DialogTitle>
            <DialogContent
                sx={{
                    overflow: 'hidden',
                    pr: 1,
                    '& > div': {
                        pr: 2,
                        maxHeight: 'calc(100vh - 212px)',
                    },
                }}
            >
                <PerfectScrollbar>
                    <Form id={formId}>
                        <Stack
                            direction="column"
                            gap={2}
                            sx={{ pt: 2, width: { xs: '100%', sm: '340px' } }}
                        >
                            <Select
                                name="vehicleGroup"
                                label="Transportation Mode"
                                placeholder="Select Transportation Mode"
                                options={vehicleGroupOptions}
                                showErrors
                                width="100%"
                            />
                            {!!selectedVehicleGroup && (
                                <Select
                                    name="type"
                                    label={`${selectedVehicleGroup} Type`}
                                    placeholder={`Select ${selectedVehicleGroup} Type`}
                                    options={availableAssetTypes}
                                    showErrors
                                    width="100%"
                                />
                            )}
                            <TextField
                                placeholder="Type Make"
                                name="make"
                                label="Make"
                                showErrors
                                fullWidth
                            />
                            <TextField
                                placeholder="Type Model"
                                name="model"
                                label="Model"
                                showErrors
                                fullWidth
                            />
                            <TextField
                                placeholder="Type Year"
                                name="year"
                                label="Year"
                                regExp={/^\+?([0-9 ]+)?$/}
                                showErrors
                                fullWidth
                            />
                            <TextField
                                placeholder="Type License Plate"
                                name="licensePlate"
                                label="License Plate"
                                showErrors
                                fullWidth
                            />
                            <TextField
                                placeholder="Type Mileage Start Value"
                                name="mileageStart"
                                label="Mileage Start"
                                regExp={/^\+?([0-9 ]+)?$/}
                                showErrors
                                fullWidth
                            />
                            <TextField
                                placeholder="Type Max Passengers"
                                name="maxPassengers"
                                label="Max Passengers"
                                regExp={/^\+?([0-9 ]+)?$/}
                                showErrors
                                fullWidth
                            />
                            <Select
                                name="fuelType"
                                label="Fuel Type"
                                placeholder="Select Fuel Type"
                                options={fuelOptions}
                                showErrors
                                width="100%"
                            />
                        </Stack>
                    </Form>
                </PerfectScrollbar>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    color="neutral"
                    variant="contained"
                    onClick={handleDiscard}
                    sx={{
                        px: '26px',
                        py: '10px',
                    }}
                >
                    Discard
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    type="submit"
                    form={formId}
                    disabled={!!errorFields.length}
                    sx={{
                        py: '10px',
                        '&&&': { pointerEvents: 'initial' },
                    }}
                >
                    Add Asset
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAssetModal;
